import type { AuthConfig, JWT, OAuthConfig } from '@aws-amplify/core'
import { Amplify } from 'aws-amplify'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity'
import { getCognitoRegion } from '.'

const {
  VITE_COGNITO_IDENTITY_POOL_ID,
  VITE_COGNITO_USER_POOL_ID,
  VITE_COGNITO_USER_POOL_WEB_CLIENT_ID,
  VITE_COGNITO_MANDATORY_SIGN_IN,
  VITE_COGNITO_OAUTH_DOMAIN,
  VITE_COGNITO_OAUTH_CALLBACK_URLS,
  VITE_COGNITO_OAUTH_LOGOUT_URLS,
} = import.meta.env

if (!VITE_COGNITO_USER_POOL_ID) throw new Error('Missing USER_POOL_ID')

export function configureAmplify() {
  const oauthEnabled =
    VITE_COGNITO_OAUTH_DOMAIN &&
    VITE_COGNITO_OAUTH_CALLBACK_URLS &&
    VITE_COGNITO_OAUTH_LOGOUT_URLS

  const oauthConfig: OAuthConfig | undefined = oauthEnabled
    ? {
      domain: VITE_COGNITO_OAUTH_DOMAIN,
      // aws.cognito.signin.user.admin came from here: https://github.com/aws-amplify/amplify-js/issues/13119#issuecomment-2007118558
      scopes: ['openid', 'profile', 'email', 'aws.cognito.signin.user.admin'],
      // The env var names come from cognito user pool configuration
      redirectSignIn: VITE_COGNITO_OAUTH_CALLBACK_URLS.split(',').map((url: string) => url.trim()),
      redirectSignOut: VITE_COGNITO_OAUTH_LOGOUT_URLS.split(',').map((url: string) => url.trim()),
      responseType: 'code',
      providers: ['Google'],
    }
    : undefined

  const authConfig: AuthConfig = {
    Cognito: {
      identityPoolId: VITE_COGNITO_IDENTITY_POOL_ID!,
      allowGuestAccess: VITE_COGNITO_MANDATORY_SIGN_IN !== 'true',
      userPoolId: VITE_COGNITO_USER_POOL_ID!,
      userPoolClientId: VITE_COGNITO_USER_POOL_WEB_CLIENT_ID!,

      loginWith: {
        username: true,
        email: true,
        ...(oauthConfig && { oauth: oauthConfig }),
      },
    },
  }

  Amplify.configure({ Auth: authConfig })
}

export async function getAwsCredentials(idToken?: JWT | string) {
  if (!idToken) return

  const userPoolId = VITE_COGNITO_USER_POOL_ID
  const region = getCognitoRegion(userPoolId)
  const IdentityPoolId = VITE_COGNITO_IDENTITY_POOL_ID

  if (!userPoolId || !region || !IdentityPoolId) {
    console.error('Missing required Cognito configuration')
    return
  }

  const cognitoIdentityClient = new CognitoIdentityClient({ region })

  const credentials = fromCognitoIdentityPool({
    client: cognitoIdentityClient,
    identityPoolId: IdentityPoolId,
    logins: {
      [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken.toString(),
    },
  })

  return credentials
}
